/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {Fragment, memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useKeyboard} from '../../../../hooks/keyboard';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useSelector} from 'react-redux';
import {
    AlarmControlParameters, BRIDGE_DEVICE_TYPE_ID_ST, BRIDGE_DEVICE_TYPE_ID_STPM, BRIDGE_DEVICE_TYPE_ID_STS_PLUS,
    CONTROL_TYPE_ID_ALARM,
    CONTROL_TYPE_ID_DIMMER,
    CONTROL_TYPE_ID_SIREN,
    CONTROL_TYPE_ID_THERMOSTAT,
    ControlModel,
    ControlParameters,
    ControlRenderType,
    ControlTypeModel,
    DeviceParameters,
    DimmerControlParameters,
    INPUT_TYPE_ID_MOMENTARY,
    INPUT_TYPE_ID_SENSOR,
    IOControlParameters,
    PulseControlParameters,
    SimpleControlParameters,
    SirenControlParameters,
    TemperatureControlSettings,
    ThreePhaseControlParameters,
} from '../../../../types/control';
import {SelectItem} from '../../../../components/Select';
import {BridgeDeviceModel} from '../../../../types/bridge-device';
import {SettingCheckBoxComponent} from '../../../../components/Settings/SettingCheckboxComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {AreaModel} from '../../../../store/control/state';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {
    isValidDuration,
    isValidMaxDimmerValue,
    isValidMinDimmerValue,
    isValidPulseDuration,
} from '../../../../utils/validation';

import {CONTROL_WRAPPER, useControlSelector} from '../../../../hooks/control-selector';
import {useGlassControl} from '../../../../hooks/glass-control';
import {useMap} from '../../../../hooks/map';
import {SettingsDeleteButton} from '../../../../components/Settings/SettingsDeleteButton';

const BACK_URL = '/settings/control';
const INPUT_EDIT_URL = '/settings/input/edit';

const CONTROL_TYPE_VALUE_MAP: Record<ControlRenderType, SelectItem<number>[]> = {
    complex: [
        {
            value: 0,
            label: 'Kapat(Dur)',
        },
        {
            value: 1,
            label: 'Aç(Yukarı)',
        },
        {
            value: 2,
            label: 'Aşağı',
        },
        {
            value: 3,
            label: 'Aç/Kapat',
        },
    ],
    simple: [
        {
            value: 0,
            label: 'Kapat',
        },
        {
            value: 1,
            label: 'Aç',
        },
    ],
    three_phase: [
        {
            value: 0,
            label: 'Dur',
        },
        {
            value: 1,
            label: 'Yukarı',
        },
        {
            value: 2,
            label: 'Aşağı',
        },
    ],
    two_phase: [
        {
            value: 1,
            label: 'Yukarı',
        },
        {
            value: 2,
            label: 'Aşağı',
        },
    ],
    pulse: [
        {
            value: 0,
            label: 'Pasif',
        },
        {
            value: 1,
            label: 'Aktif',
        },
    ],
};

const ALARM_LEVEL_LIST: SelectItem<number>[] = [
    {
        value: 1,
        label: 'Seviye 1',
    },
    {
        value: 2,
        label: 'Seviye 2',
    },
    {
        value: 3,
        label: 'Seviye 3',
    },
];

const SIREN_LEVEL_LIST: SelectItem<number>[] = [
    {
        value: 0,
        label: 'Hepsi',
    },
    {
        value: 1,
        label: 'Seviye 1',
    },
    {
        value: 2,
        label: 'Seviye 2',
    },
    {
        value: 3,
        label: 'Seviye 3',
    },
];

const SIREN_LIST: SelectItem<number>[] = [
    {
        value: 0,
        label: 'İnaktif',
    },
    {
        value: 1,
        label: 'Fasılalı',
    },
    {
        value: 2,
        label: 'Sürekli',
    },
];

const HEATING_MODE_LIST: SelectItem<number>[] = [
    {
        value: 1,
        label: 'Isıtma',
    },
    {
        value: 2,
        label: 'Soğutma',
    },
    {
        value: 3,
        label: 'Isıtma/Soğutma',
    },
];

export const NO_OUTPUT_OPTION: SelectItem<number> = {value: 99, label: 'Çıkış Verme'};

type ALARM_CONTROL_VALUE = {
    id: string;
    isVirtual: boolean;
};

export const ControlEditScreen = memo(function ControlEditScreen() {
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();
    const location = useLocation<ControlModel>();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] =
        useKeyboard('numpad-extended', true);

    const controlList = useControlSelector();
    const controlSet = useSetSelector(state => state.control.controlList);
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList);
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);

    const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] =
        useMemo(() => bridgeDeviceList
            .filter(bridgeDevice => (bridgeDeviceTypeSet[bridgeDevice.type_id]?.output_count || 0) > 0)
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
            .map(bridgeDevice => ({
                value: bridgeDevice,
                label: bridgeDevice.name,
            })), [bridgeDeviceList, bridgeDeviceTypeSet]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => location.state || controlSet[params?.id] || null, []);
    const [bridgeDevice, setBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? null :
            bridgeDeviceOptionList.find(bridgeDeviceOption => bridgeDeviceOption.value.id === selected.bridge_device_id)
            || null),
    );
    const bridgeDeviceType = useMemo(() => bridgeDevice == null ? null : bridgeDeviceTypeSet[bridgeDevice.value.type_id],
        [bridgeDevice, bridgeDeviceTypeSet]);
    const bridgeDeviceDimmerCount = useMemo(() => bridgeDeviceType?.dimmer_count || 0, [bridgeDeviceType]);
    const isBridgeDeviceTypeIO = useMemo(() => bridgeDeviceType?.is_io_device || false, [bridgeDeviceType]);
    const usedOutputList = useMemo(() => {
        const list = [];
        for (const control of Object.values(controlSet)) {
            if (control.bridge_device_id !== bridgeDevice?.value.id) {
                continue;
            }

            list.push(control.parameters.output_number);

            if (controlTypeSet[control.type_id].render_type === 'three_phase' || controlTypeSet[control.type_id].render_type === 'two_phase') {
                list.push((control.parameters as ThreePhaseControlParameters).down_output_number);
            }
        }

        return list;
    }, [bridgeDevice, controlSet, controlTypeSet]);

    const alarmControlOptionList: SelectItem<CONTROL_WRAPPER>[] = useMemo(() => {
            const controls = controlList
                .filter(control => control.bridgeDeviceId === bridgeDevice?.value?.id && control.control.type_id !== CONTROL_TYPE_ID_ALARM && control.control.type_id !== CONTROL_TYPE_ID_SIREN)
                .map(control => ({label: control.control.name, value: control}));

            controls.unshift({
                label: 'Çıkış verme', value: {
                    id: undefined,
                    isVirtual: false,
                    bridgeDeviceId: '',
                    glassControlParameters: undefined,
                    control: undefined,
                },
            });

            return controls;
        },
        [controlList, bridgeDevice?.value]);
    const outputNumberOptionList: SelectItem<number>[] = useMemo(() => {
        const outputCount = bridgeDeviceType?.output_count || 0;
        if (outputCount === 0) {
            return [];
        }

        const list = [];
        list.push(NO_OUTPUT_OPTION);
        for (let i = 0; i < outputCount; i++) {
            //@ts-ignore
            const isUsedOutput = usedOutputList.some((o) => o === i && (o !== selected?.parameters.output_number && o !== selected?.parameters.down_output_number));
            list.push({value: i, label: `Çıkış ${i.toString()}${isUsedOutput ? '*' : ''}`});
        }

        return list;
    }, [bridgeDeviceType, usedOutputList, selected?.parameters]);
    const doubledOutputNumberOptionList: SelectItem<number>[] = useMemo(() => {
        const outputCount = bridgeDeviceType?.output_count || 0;
        if (outputCount === 0) {
            return [];
        }

        const list = [];
        list.push(NO_OUTPUT_OPTION);
        for (let i = 0; i + 1 < outputCount; i += 2) {
            //@ts-ignore
            const isUsedOutput = usedOutputList.some((o) => o === i && (o !== selected?.parameters.output_number && o !== selected?.parameters.down_output_number));
            list.push({value: i, label: `Çıkış ${i.toString()} - ${(i + 1).toString()}${isUsedOutput ? '*' : ''}`});
        }

        return list;
    }, [bridgeDeviceType, usedOutputList, selected?.parameters]);
    const controlTypeList = useSelector(state => state.control.controlTypeList);
    const controlTypeOptionList: SelectItem<Omit<ControlTypeModel, 'image_set'>>[] =
        useMemo(() => controlTypeList
            .filter(controlType =>
                controlType.allowed_bridge_device_type_id_list.indexOf(bridgeDevice?.value?.type_id) !== -1)
            .filter(controlType =>
                controlType.id !== CONTROL_TYPE_ID_DIMMER || bridgeDeviceDimmerCount > 0)
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
            .map(controlType => ({
                value: controlType,
                label: controlType.name,
            })), [bridgeDeviceDimmerCount, bridgeDevice?.value?.type_id, controlTypeList]);

    const [name, setName] = useState(selected?.id != null ? selected?.name : '');
    const [controlType, setControlType] = useState<SelectItem<Omit<ControlTypeModel, 'image_set'>>>(
        (selected == null ? null : controlTypeOptionList.find(controlTypeOption => controlTypeOption.value.id === selected.type_id)
            || null),
    );
    const [outputReverse, setOutputReverse] = useState((selected?.parameters as SimpleControlParameters)?.should_output_reverse || false);
    const [rememberLastValue, setRememberLastValue] = useState((selected?.parameters as IOControlParameters)?.should_remember_last_value || false);
    const [navigateToSTPEditor, setNavigateToSTPEditor] = useState<boolean>();

    const thermostatBridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMap(
        useFilterSelector(
            state => state.control.bridgeDeviceList,
            bridgeDeviceItem =>
                bridgeDeviceTypeSet[bridgeDeviceItem.type_id]?.is_temperature_device === true &&
                bridgeDevice?.value != null &&
                bridgeDevice.value.gateway_id === bridgeDeviceItem.gateway_id &&
                controlList.find(c =>
                    c.isVirtual === false &&
                    (c.control.temperature_settings?.bridge_device_id === bridgeDeviceItem.id && bridgeDeviceItem.id !== selected?.temperature_settings?.bridge_device_id)) == null,
            [bridgeDeviceTypeSet, bridgeDevice?.value, controlList, selected],
        ),
        bridgeDevice => ({value: bridgeDevice, label: bridgeDevice.name}),
        [],
    );

    const [thermostatBridgeDevice, setThermostatBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? null :
            thermostatBridgeDeviceOptionList.find(b => b.value.id === selected.temperature_settings?.bridge_device_id)
            || null),
    );
    const thermostatBridgeDeviceType = useMemo(() => thermostatBridgeDevice == null ? null : bridgeDeviceTypeSet[thermostatBridgeDevice.value.type_id],
        [thermostatBridgeDevice, bridgeDeviceTypeSet]);
    // noinspection JSUnusedLocalSymbols
    const isThermostatBridgeDeviceIO = useMemo(() => thermostatBridgeDeviceType == null || thermostatBridgeDeviceType?.is_io_device, [thermostatBridgeDeviceType]);

    const [addInput, setAddInput] = useState(false);

    const {
        buttonNumberOptionList,
        setButtonNumber,
        buttonNumber,
    } =
        useGlassControl(selected, bridgeDevice?.value?.id || null, bridgeDeviceType, controlType?.value?.id);

    const areaList = useFilterSelector(state => state.control.areaList, area => area.type === 'room' || area.type === 'place', []);
    const areaOptionList: SelectItem<AreaModel>[] =
        useMemo(() => areaList.map(area => ({value: area, label: area.name})), [areaList]);
    const [areaOption, setAreaOption] = useState<SelectItem<AreaModel>>(
        (selected == null ? null : areaOptionList.find(areaOption => areaOption.value.id === selected.area_id)
            || null),
    );
    const [outputNumber, setOutputNumber] = useState<SelectItem<number>>(
        (selected == null ||
        (!isBridgeDeviceTypeIO && (controlType?.value.render_type === 'three_phase' || controlType?.value.render_type === 'two_phase')) ? null :
            outputNumberOptionList.find(outputNumberOption => outputNumberOption.value === selected.parameters?.output_number)
            || null),
    );
    const [downOutputNumber, setDownOutputNumber] = useState<SelectItem<number>>(
        (selected == null ||
        (!isBridgeDeviceTypeIO && (controlType?.value.render_type === 'three_phase' || controlType?.value.render_type === 'two_phase')) ? null :
            outputNumberOptionList.find(outputNumberOption => outputNumberOption.value === (selected.parameters as ThreePhaseControlParameters)?.down_output_number)
            || null),
    );
    const [doubledOutputNumber, setDoubledOutputNumber] = useState<SelectItem<number>>(
        (selected == null || isBridgeDeviceTypeIO || (controlType?.value.render_type !== 'three_phase' && controlType?.value.render_type !== 'two_phase') ? null :
            doubledOutputNumberOptionList.find(outputNumberOption => outputNumberOption.value === selected.parameters?.output_number)
            || null),
    );
    const [outputDurationLimitSeconds, setOutputDurationLimitSeconds] = useState(
        (selected?.parameters as ThreePhaseControlParameters)?.output_time_limit?.toString()
        || '');
    const [pulseDuration, setPulseDuration] = useState(
        (((selected?.parameters as PulseControlParameters)?.pulse_time || 0) / 10).toString()
        || '');
    const [maxValue, setMaxValue] = useState((selected?.parameters as DimmerControlParameters)?.max_limit?.toString()
        || '');
    const [minValue, setMinValue] = useState((selected?.parameters as DimmerControlParameters)?.min_limit?.toString()
        || '');
    const [alarmLevelOption, setAlarmLevelOption] = useState<SelectItem<number>>(
        selected == null ? ALARM_LEVEL_LIST[0] : (
            ALARM_LEVEL_LIST.find(alarmLevelOption => alarmLevelOption.value === (selected.parameters as SirenControlParameters)?.alarm_level)
            || null),
    );
    const [sirenLevelOption, setSirenLevelOption] = useState<SelectItem<number>>(
        selected == null ? SIREN_LEVEL_LIST[0] : (
            SIREN_LEVEL_LIST.find(sirenLevelOption => sirenLevelOption.value === (selected.parameters as SirenControlParameters)?.alarm_level)
            || null),
    );
    const [alarmControlOption, setAlarmControlOption] = useState<SelectItem<ALARM_CONTROL_VALUE>>(
        selected == null ? null : (
            alarmControlOptionList.find(option => option.value.id === (selected?.parameters as AlarmControlParameters)?.relational_control_id)
        ) || null,
    );
    const [alarmSirenOption, setAlarmSirenOption] = useState<SelectItem<number>>(
        selected == null ? SIREN_LIST[0] : (
            SIREN_LIST.find(sirenOption => sirenOption.value === (selected.parameters as AlarmControlParameters)?.siren_type)
            || null),
    );
    const [heatingModeOption, setHeatingModeOption] = useState<SelectItem<number>>(
        selected == null ? HEATING_MODE_LIST[0] : (
            HEATING_MODE_LIST.find(heatingOption => heatingOption.value === (
                selected.temperature_settings?.has_cooling && selected.temperature_settings?.has_heating ? 3 :
                    (selected.temperature_settings?.has_cooling ? 2 : 1)
            ))
            || null),
    );
    const [alarmDelaySeconds, setAlarmDelaySeconds] = useState(
        (selected?.parameters as AlarmControlParameters)?.delay_time?.toString()
        || '');

    const defaultValueOptionList = useMemo(() => {
        if (!controlType) {
            return [];
        }

        return CONTROL_TYPE_VALUE_MAP[controlType.value.render_type];
    }, [controlType]);
    const [defaultValueOption, setDefaultValueOption] = useState<SelectItem<number>>(
        (selected == null ? null : defaultValueOptionList.find(defaultValueOption => defaultValueOption.value === (selected.parameters as IOControlParameters)?.default_value))
        || defaultValueOptionList[0] || null,
    );

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const goToInputEditScreenWithState = useCallback((state: unknown) => {
        history.push({pathname: INPUT_EDIT_URL, state});
    }, [history]);

    const goToStpEditorWithState = useCallback((state: unknown) => {
        //     if (controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT) {
        //         state['controlId'] = controlList.find(c => c.isVirtual === true && state['controlId'] === c.control.id)?.id || state['controlId'];
        //     }
        // todo above not working, as a result thermostat virtualControl's stpUIEntity page cannot be redirected
        history.push({pathname: `/settings/stp-editor/${bridgeDevice.value.id}/content/`, state});
    }, [bridgeDevice, history/*, controlType, controlList*/]);

    const onDeletePressed = useCallback(() => {
        return rpc.deleteControl(selected?.id)
            .then(() => {
                success('Kontrol başarılı bir şekilde silindi.');
                onBackPressed();
            })
            .catch(failure);
    }, [failure, rpc, success, onBackPressed, selected?.id]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure('Lütfen kontrol adı giriniz.');

            return false;
        }

        if (bridgeDevice?.value == null) {
            failure('Lütfen cihaz seçiniz.');

            return false;
        }

        if (controlType == null) {
            failure('Lütfen kontrol tipi seçiniz.');

            return false;
        }

        if (areaOption?.value == null) {
            failure('Lütfen alan seçiniz.');

            return false;
        }

        if (controlType.value.id === CONTROL_TYPE_ID_ALARM) {
            if (alarmControlOption?.value == null) {
                failure('Lütfen kontrol seçiniz.');

                return false;
            }

            if (alarmSirenOption?.value == null) {
                failure('Lütfen siren seçiniz.');

                return false;
            }

            if (alarmLevelOption?.value == null) {
                failure('Lütfen alarm seviyesi seçiniz.');

                return false;
            }

            if (!isValidDuration(alarmDelaySeconds)) {
                failure('Lütfen geçerli gecikme süresi giriniz.');

                return false;
            }
        } else if (controlType.value.id === CONTROL_TYPE_ID_SIREN) {
            if (outputNumber?.value == null) {
                failure('Lütfen çıkış no seçiniz.');

                return false;
            }

            if (sirenLevelOption?.value == null) {
                failure('Lütfen alarm seviyesi seçiniz.');

                return false;
            }
        } else if (controlType.value.id === CONTROL_TYPE_ID_THERMOSTAT) {
            if (isBridgeDeviceTypeIO && thermostatBridgeDevice?.value == null) {
                failure('Lütfen sensöz cihazı seçiniz.');

                return false;
            }

            if (!isBridgeDeviceTypeIO) {
                const foundControl = controlList.find(c => c.isVirtual === false &&
                    c.control.id !== selected?.id &&
                    c.control.temperature_settings?.bridge_device_id === bridgeDevice.value.id,
                );
                if (foundControl != null) {
                    failure(`${bridgeDevice.value.name} adlı cihaza daha önce termostat (${foundControl.control.name}) eklendi.`);

                    return false;
                }

            }

            if (!isBridgeDeviceTypeIO) {
                if (heatingModeOption?.value === 2 || heatingModeOption?.value === 3) {
                    if (downOutputNumber?.value == null) {
                        failure('Lütfen soğuk çıkış no seçiniz.');

                        return false;
                    }
                }


                if (heatingModeOption?.value === 1 || heatingModeOption?.value === 3) {
                    if (outputNumber?.value == null) {
                        failure('Lütfen sıcak çıkış no seçiniz.');

                        return false;
                    }
                }
            } else {
                if (outputNumber?.value == null) {
                    failure('Lütfen çıkış no seçiniz.');

                    return false;
                }
            }
        } else {
            if (isBridgeDeviceTypeIO) {
                switch (controlType.value.render_type) {
                    case 'simple':
                        if (outputNumber?.value == null) {
                            failure('Lütfen çıkış no seçiniz.');

                            return false;
                        }

                        if (!rememberLastValue && defaultValueOption?.value == null) {
                            failure('Lütfen varsayılan değer seçiniz.');

                            return false;
                        }
                        break;
                    case 'two_phase':
                    case 'three_phase':
                        if (outputNumber?.value == null) {
                            failure('Lütfen yukarı çıkış no seçiniz.');

                            return false;
                        }

                        if (downOutputNumber?.value == null) {
                            failure('Lütfen aşağı çıkış no seçiniz.');

                            return false;
                        }

                        if (outputNumber.value !== NO_OUTPUT_OPTION.value &&
                            outputNumber.value === downOutputNumber.value) {
                            failure('Yukarı ve aşağı çıkış numaraları aynı olamaz.');

                            return false;
                        }

                        if (!isValidDuration(outputDurationLimitSeconds)) {
                            failure('Lütfen geçerli bir çıkış süre limiti yazınız.');

                            return false;
                        }

                        if (!rememberLastValue && defaultValueOption?.value == null) {
                            failure('Lütfen varsayılan değer seçiniz.');

                            return false;
                        }

                        break;
                    case 'pulse':
                        if (outputNumber?.value == null) {
                            failure('Lütfen yukarı çıkış no seçiniz.');

                            return false;
                        }

                        if (!isValidPulseDuration(pulseDuration)) {
                            failure('Lütfen geçerli pulse süresi giriniz.');

                            return false;
                        }

                        if (!rememberLastValue && defaultValueOption?.value == null) {
                            failure('Lütfen varsayılan değer seçiniz.');

                            return false;
                        }

                        break;
                    case 'complex':
                        if (!rememberLastValue && defaultValueOption?.value == null) {
                            failure('Lütfen varsayılan değer seçiniz.');

                            return false;
                        }

                        failure('Tanımsız kontrol tipi.');

                        return false;
                    default:
                        break;
                }
            } else {
                if (bridgeDeviceType?.button_count > 0) {
                    if (buttonNumber?.value == null) {
                        failure('Lütfen buton numarası seçiniz.');

                        return false;
                    }
                }

                switch (controlType.value.render_type) {
                    case 'simple':
                        if (outputNumber?.value == null) {
                            failure('Lütfen çıkış no seçiniz.');

                            return false;
                        }

                        break;
                    case 'two_phase':
                    case 'three_phase':
                        if (doubledOutputNumber?.value == null) {
                            failure('Lütfen çıkış no seçiniz.');

                            return false;
                        }

                        if (!isValidDuration(outputDurationLimitSeconds)) {
                            failure('Lütfen geçerli bir çıkış süre limiti yazınız.');

                            return false;
                        }

                        break;
                    case 'pulse':
                        // no validations
                        break;
                    case 'complex':
                        if (controlType.value.id === CONTROL_TYPE_ID_DIMMER) {
                            if (outputNumber?.value == null) {
                                failure('Lütfen çıkış no seçiniz.');

                                return false;
                            }

                            if (!isValidMaxDimmerValue(maxValue)) {
                                failure('Lütfen geçerli maksimum değer giriniz.');

                                return false;
                            }

                            if (!isValidMinDimmerValue(minValue)) {
                                failure('Lütfen geçerli minimum değer giriniz.');

                                return false;
                            }

                            if (Number(maxValue) <= Number(minValue)) {
                                failure('Maksimum değer minimum değerden büyük olmak zorundadır.');

                                return false;
                            }
                        } else {
                            failure('Tanımsız kontrol tipi.');

                            return false;
                        }

                        break;
                    default:
                        break;
                }
            }
        }

        return true;
    }, [
        failure,
        name,
        bridgeDevice,
        bridgeDeviceType,
        areaOption,
        isBridgeDeviceTypeIO,
        controlType,
        outputNumber?.value,
        rememberLastValue,
        defaultValueOption,
        buttonNumber?.value,
        outputDurationLimitSeconds,
        downOutputNumber?.value,
        doubledOutputNumber?.value,
        pulseDuration,
        maxValue,
        minValue,
        alarmLevelOption?.value,
        alarmControlOption?.value,
        alarmSirenOption?.value,
        alarmDelaySeconds,
        thermostatBridgeDevice?.value,
        heatingModeOption?.value,
        sirenLevelOption?.value,
        controlList,
        selected?.id,
    ]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        let ioOrGlassParameters: DeviceParameters;
        if (isBridgeDeviceTypeIO) {
            ioOrGlassParameters = {
                should_output_reverse: outputReverse,
                should_remember_last_value: rememberLastValue,
                default_value: defaultValueOption?.value,
                output_number: outputNumber?.value,
            };
        } else {
            ioOrGlassParameters = {
                output_number: outputNumber?.value,
                button_number: buttonNumber?.value,
            };
        }

        let controlParameters: ControlParameters = null;
        let temperatureSettings: TemperatureControlSettings = null;
        switch (controlType.value.render_type) {
            case 'simple':
                controlParameters = {
                    should_output_reverse: outputReverse,
                };
                break;
            case 'two_phase':
            case 'three_phase':
                controlParameters = {
                    should_output_reverse: outputReverse,
                    down_output_number: isBridgeDeviceTypeIO ? downOutputNumber.value :
                        doubledOutputNumber.value === NO_OUTPUT_OPTION.value ? NO_OUTPUT_OPTION.value :
                            doubledOutputNumber.value + 1,
                    output_time_limit: Number(outputDurationLimitSeconds),
                };
                if (!isBridgeDeviceTypeIO) {
                    ioOrGlassParameters.output_number = doubledOutputNumber.value;
                }

                break;
            case 'pulse':
                controlParameters = {
                    pulse_time: Number(pulseDuration) * 10,
                };
                break;
            case 'complex':
                switch (controlType.value.id) {
                    case CONTROL_TYPE_ID_DIMMER:
                        controlParameters = {
                            min_limit: Number(minValue),
                            max_limit: Number(maxValue),
                        };
                        break;
                    case CONTROL_TYPE_ID_ALARM:
                        controlParameters = {
                            is_virtual_control: alarmControlOption.value.isVirtual,
                            relational_control_id: alarmControlOption.value.id,
                            siren_type: alarmSirenOption.value,
                            alarm_level: alarmLevelOption.value,
                            delay_time: Number(alarmDelaySeconds),
                        };
                        break;
                    case CONTROL_TYPE_ID_SIREN:
                        controlParameters = {
                            alarm_level: sirenLevelOption.value,
                        };
                        break;
                    case CONTROL_TYPE_ID_THERMOSTAT:
                        temperatureSettings = {
                            bridge_device_id: isBridgeDeviceTypeIO ? thermostatBridgeDevice.value.id : bridgeDevice.value.id,
                            is_mode_heating: heatingModeOption.value === 1 || heatingModeOption.value === 3,
                            has_heating: heatingModeOption.value === 1 || heatingModeOption.value === 3,
                            has_cooling: heatingModeOption.value === 2 || heatingModeOption.value === 3,
                        };

                        if (temperatureSettings.has_cooling && !isBridgeDeviceTypeIO) {
                            controlParameters = {
                                ...controlParameters,
                                down_output_number: downOutputNumber.value,
                            };
                            if (!temperatureSettings.has_heating) {
                                ioOrGlassParameters = {
                                    ...ioOrGlassParameters,
                                    output_number: downOutputNumber.value,
                                };
                            }
                        }

                        break;
                    default:
                        failure('Tanımsız kontrol tipi.');

                        return;
                }

                break;
            default:
                break;
        }

        const controlModel: ControlModel = {
            ...(selected ? selected : {}),
            id: selected?.id || null,
            name,
            area_id: areaOption.value.id,
            bridge_device_id: bridgeDevice.value.id,
            type_id: controlType.value.id,
            parameters: {...ioOrGlassParameters, ...controlParameters},
            temperature_settings: temperatureSettings,
        };

        return rpc.updateControl(controlModel)
            .then((id: string) => {
                if (navigateToSTPEditor) {
                    goToStpEditorWithState({
                        controlId: id,
                        bridgeDeviceId: bridgeDevice.value.id,
                    });
                } else if (addInput) {
                    goToInputEditScreenWithState({
                        controlId: id,
                        bridgeDeviceId: bridgeDevice.value.id,
                        inputTypeId: controlType.value.id === CONTROL_TYPE_ID_ALARM ? INPUT_TYPE_ID_SENSOR : INPUT_TYPE_ID_MOMENTARY,
                    });
                } else {
                    success('Kontrol başarılı bir şekilde ' + (selected?.id == null ? 'kaydedildi.' : 'düzenlendi.'));
                    onBackPressed();
                }
            })
            .catch(failure);
    }, [
        isFieldsValid,
        rpc,
        success,
        failure,
        onBackPressed,
        goToInputEditScreenWithState,
        addInput,
        selected,
        name,
        bridgeDevice,
        areaOption,
        isBridgeDeviceTypeIO,
        controlType,
        rememberLastValue,
        defaultValueOption,
        outputReverse,
        outputNumber?.value,
        buttonNumber?.value,
        downOutputNumber?.value,
        outputDurationLimitSeconds,
        doubledOutputNumber?.value,
        pulseDuration,
        maxValue,
        minValue,
        sirenLevelOption?.value,
        alarmLevelOption?.value,
        alarmSirenOption?.value,
        alarmDelaySeconds,
        alarmControlOption?.value,
        thermostatBridgeDevice?.value?.id,
        heatingModeOption?.value,
        navigateToSTPEditor,
        goToStpEditorWithState,
    ]);

    const setOutputReverseCheckbox = useCallback(() => {
        setOutputReverse(!outputReverse);
    }, [outputReverse]);

    const setRememberLastValueCheckbox = useCallback(() => {
        setRememberLastValue(rememberLastValue => !rememberLastValue);
    }, []);

    const setAddInputCheckbox = useCallback(() => {
        setAddInput(addInput => !addInput);
    }, []);

    const setNavigateStpEditorCheckbox = useCallback(() => {
        setNavigateToSTPEditor(!navigateToSTPEditor);
    }, [navigateToSTPEditor]);


    const buttons = useMemo(() => [
        {
            component: params?.id == null ? null :
                <SettingsDeleteButton
                    id={selected?.id}
                    type="control"
                    questionText="Kontrol silmek istediğinizden emin misiniz?"
                    questionTextRelational={message => `${selected?.name} ile beraber ${message} silinecektir. Devam edilsin mi?`}
                    onPress={onDeletePressed}/>,
        },
        {
            text: 'KAYDET',
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: 'İPTAL',
            className: 'red',
            url: BACK_URL,
        },
    ], [onCreatePressed, onDeletePressed, selected, params?.id]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
        setActiveNumpadExtendedInput(null);
    }, [setActiveInput, setActiveNumpadExtendedInput, setActiveNumpadInput]);

    useEffect(() => {
        setControlType(controlTypeOptionList.find(option => option.value === controlType?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlTypeOptionList]);

    useEffect(() => {
        setDefaultValueOption(defaultValueOptionList.find(option => option.value === defaultValueOption?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValueOptionList]);

    useEffect(() => {
        setOutputNumber(outputNumberOptionList.find(option => option.value === outputNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outputNumberOptionList]);

    useEffect(() => {
        setDownOutputNumber(outputNumberOptionList.find(option => option.value === downOutputNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outputNumberOptionList]);

    useEffect(() => {
        setDoubledOutputNumber(doubledOutputNumberOptionList.find(option => option.value === doubledOutputNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doubledOutputNumberOptionList]);

    useEffect(() => {
        setAlarmControlOption(alarmControlOptionList.find(option => option.value === alarmControlOption?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alarmControlOptionList]);

    useEffect(() => {
        if (thermostatBridgeDeviceType?.id == null) {
            return;
        }

        if (thermostatBridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_ST) {
            setHeatingModeOption(HEATING_MODE_LIST[0]);
        }
    }, [thermostatBridgeDeviceType?.id]);

    useEffect(() => {
        if (!isBridgeDeviceTypeIO) {
            setThermostatBridgeDevice(thermostatBridgeDeviceOptionList.find(b => b.value.id === bridgeDevice.value.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bridgeDevice?.value?.id]);

    return (
        <PasswordContainer requiredRole="user" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder="Kontrol adı yazınız..."
                            label="Kontrol Adı"
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label="Cihaz"
                            options={bridgeDeviceOptionList}
                            onChange={setBridgeDevice}
                            value={bridgeDevice}/>
                        {
                            renderIf(bridgeDevice)(() =>
                                <>
                                    <SettingSelectComponent
                                        label="Kontrol Tipi"
                                        options={controlTypeOptionList}
                                        onChange={setControlType}
                                        value={controlType}/>
                                    {
                                        renderIf(isBridgeDeviceTypeIO && controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT)(() =>
                                            <>
                                                <SettingSelectComponent
                                                    label="Sensör Cihazı"
                                                    options={thermostatBridgeDeviceOptionList}
                                                    onChange={setThermostatBridgeDevice}
                                                    value={thermostatBridgeDevice}/>
                                            </>,
                                        )
                                    }
                                    {
                                        renderIf(thermostatBridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_ST && thermostatBridgeDevice?.value && controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT)(() =>
                                            <SettingSelectComponent
                                                label="Mod"
                                                options={HEATING_MODE_LIST}
                                                onChange={setHeatingModeOption}
                                                value={heatingModeOption}/>,
                                        )
                                    }
                                    {
                                        renderIf(controlType?.value.id === CONTROL_TYPE_ID_ALARM)(() =>
                                            <>
                                                <SettingSelectComponent
                                                    label="Kontrol"
                                                    options={alarmControlOptionList}
                                                    onChange={setAlarmControlOption}
                                                    value={alarmControlOption}/>
                                                <SettingSelectComponent
                                                    label="Siren"
                                                    options={SIREN_LIST}
                                                    onChange={setAlarmSirenOption}
                                                    value={alarmSirenOption}/>
                                                <SettingSelectComponent
                                                    label="Alarm Seviyesi"
                                                    options={ALARM_LEVEL_LIST}
                                                    onChange={setAlarmLevelOption}
                                                    value={alarmLevelOption}/>
                                                <SettingInputComponent
                                                    placeholder="Alarm filtre süresi ..."
                                                    label="Filtre Süresi (sn)"
                                                    value={alarmDelaySeconds}
                                                    setValue={setAlarmDelaySeconds}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>, () =>
                                            renderIf(controlType?.value?.render_type === 'three_phase' || controlType?.value?.render_type === 'two_phase')(() =>
                                                [
                                                    renderIf(isBridgeDeviceTypeIO)(() =>
                                                        <Fragment key="output_no_io">
                                                            <SettingSelectComponent
                                                                label="Çıkış No (Yukarı)"
                                                                options={outputNumberOptionList}
                                                                onChange={setOutputNumber}
                                                                value={outputNumber}/>
                                                            <SettingSelectComponent
                                                                label="Çıkış No (Aşağı)"
                                                                options={outputNumberOptionList}
                                                                onChange={setDownOutputNumber}
                                                                value={downOutputNumber}/>
                                                        </Fragment>, () =>
                                                        <SettingSelectComponent
                                                            key="output_no_glass"
                                                            label="Çıkış No"
                                                            options={doubledOutputNumberOptionList}
                                                            onChange={setDoubledOutputNumber}
                                                            value={doubledOutputNumber}/>,
                                                    ),
                                                    <SettingInputComponent
                                                        key="output_duration"
                                                        placeholder="Çıkış süre limiti yazınız..."
                                                        label="Çıkış Süre Limiti (sn)"
                                                        value={outputDurationLimitSeconds}
                                                        setValue={setOutputDurationLimitSeconds}
                                                        setActiveInput={setActiveNumpadInput}
                                                        onBlur={onBlur}/>,
                                                ], () =>
                                                renderIf(!isBridgeDeviceTypeIO && controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT)(() =>
                                                    renderIf(thermostatBridgeDevice?.value)(() =>
                                                        <Fragment key="output_no_io">
                                                            {
                                                                renderIf(heatingModeOption.value === 1 || heatingModeOption.value === 3)(() =>
                                                                    <SettingSelectComponent
                                                                        label="Çıkış No (Isıtma)"
                                                                        options={outputNumberOptionList}
                                                                        onChange={setOutputNumber}
                                                                        value={outputNumber}/>,
                                                                )
                                                            }
                                                            {
                                                                renderIf(heatingModeOption.value === 2 || heatingModeOption.value === 3)(() =>
                                                                    <SettingSelectComponent
                                                                        label="Çıkış No (Soğutma)"
                                                                        options={outputNumberOptionList}
                                                                        onChange={setDownOutputNumber}
                                                                        value={downOutputNumber}/>,
                                                                )
                                                            }
                                                        </Fragment>,
                                                    ), () =>
                                                    renderIf(controlType?.value)(() =>
                                                        <SettingSelectComponent
                                                            label="Çıkış No"
                                                            options={outputNumberOptionList}
                                                            onChange={setOutputNumber}
                                                            value={outputNumber}/>,
                                                    ),
                                                ),
                                            ),
                                        )
                                    }
                                    {
                                        renderIf(isBridgeDeviceTypeIO && controlType?.value?.render_type === 'pulse')(() =>
                                            <SettingInputComponent
                                                placeholder="Pulse süresi yazınız..."
                                                label="Pulse Süresi (sn)"
                                                value={pulseDuration}
                                                setValue={setPulseDuration}
                                                setActiveInput={setActiveNumpadExtendedInput}
                                                onBlur={onBlur}/>,
                                        )
                                    }
                                    {
                                        renderIf(controlType?.value.id === CONTROL_TYPE_ID_DIMMER)(() =>
                                            <>
                                                <SettingInputComponent
                                                    placeholder="Maksimum değer yazınız..."
                                                    label="Maksimum değer"
                                                    value={maxValue}
                                                    setValue={setMaxValue}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder="Minimum değer yazınız..."
                                                    label="Minimum değer"
                                                    value={minValue}
                                                    setValue={setMinValue}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>,
                                        )
                                    }
                                    {
                                        renderIf(controlType?.value.id === CONTROL_TYPE_ID_SIREN)(() =>
                                            <SettingSelectComponent
                                                label="Alarm Seviyesi"
                                                options={SIREN_LEVEL_LIST}
                                                onChange={setSirenLevelOption}
                                                value={sirenLevelOption}/>,
                                        )
                                    }
                                </>,
                            )
                        }
                    </div>
                    <div className="content-wrapper">
                        <SettingSelectComponent
                            label="Alan"
                            options={areaOptionList}
                            onChange={setAreaOption}
                            value={areaOption}/>
                        {
                            renderIf(controlType != null && isBridgeDeviceTypeIO)(() =>
                                <SettingCheckBoxComponent
                                    label="Çıkış Tersleme"
                                    set={setOutputReverseCheckbox}
                                    value={outputReverse}/>,
                            )
                        }
                        {
                            renderIf(controlType?.value != null &&
                                controlType.value.id !== CONTROL_TYPE_ID_ALARM &&
                                controlType.value.id !== CONTROL_TYPE_ID_SIREN)(() =>
                                renderIf(isBridgeDeviceTypeIO && controlType.value.id !== CONTROL_TYPE_ID_THERMOSTAT)(() =>
                                    <>
                                        <SettingCheckBoxComponent
                                            label="Son Durumu Hatırla"
                                            set={setRememberLastValueCheckbox}
                                            value={rememberLastValue}/>
                                        {
                                            renderIf(!rememberLastValue)(() =>
                                                <SettingSelectComponent
                                                    label="Varsayılan Değer"
                                                    options={defaultValueOptionList}
                                                    onChange={setDefaultValueOption}
                                                    value={defaultValueOption}/>,
                                            )
                                        }
                                    </>, () =>
                                    <>
                                        {
                                            renderIf(bridgeDeviceType?.button_count > 0)(() =>
                                                <SettingSelectComponent
                                                    label="Buton Numarası"
                                                    options={buttonNumberOptionList}
                                                    onChange={setButtonNumber}
                                                    value={buttonNumber}/>,
                                            )
                                        }
                                    </>,
                                ))
                        }
                        {
                            renderIf(!isBridgeDeviceTypeIO && controlType?.value?.render_type === 'simple')(() =>
                                <SettingCheckBoxComponent
                                    label="Çıkış Tersleme"
                                    set={setOutputReverseCheckbox}
                                    value={outputReverse}/>,
                            )
                        }
                        {
                            renderIf(selected?.id == null &&
                                controlType?.value != null &&
                                controlType.value.id !== CONTROL_TYPE_ID_THERMOSTAT &&
                                isBridgeDeviceTypeIO)(() =>
                                <SettingCheckBoxComponent
                                    label="Giriş Ekle"
                                    set={setAddInputCheckbox}
                                    value={addInput}/>,
                            )
                        }
                        {
                            renderIf(selected?.id == null &&
                                (bridgeDeviceType?.page_count > 0
                                    && bridgeDeviceType?.id !== '6'
                                    /*||
                                    (isBridgeDeviceTypeIO && controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT && !isThermostatBridgeDeviceIO)*/))(() =>
                                <SettingCheckBoxComponent
                                    label={bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_STS_PLUS ? 'STS PLUS Editörüne Git' : 'STP Editörüne Git'}
                                    set={setNavigateStpEditorCheckbox}
                                    value={navigateToSTPEditor}/>,
                            )
                        }
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
            {
                renderNumpadExtendedKeyboard()
            }
        </PasswordContainer>
    );
});
